import { useEffect, useState } from "react";
import axios from "axios";
import HeroImg from "../Components/Layouts/HeroImg";
import { useNavigate } from "react-router-dom";
import { Filter } from "bad-words";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  deleteOrder,
  cancelOrder,
  downloadReceipt,
} from "../context/OrderContext";
import "./Order.scss";
import OrderTracking from "../Components/Other/Tracking";
import { useAuth } from "../context/AuthContext";
import { FaPen } from "react-icons/fa";
import OrderStatusDropdown from "../Components/Admin/UpdateOrder";
import { toast } from "react-toastify";
import { loadRazorpay } from "../utils/RazorpayUtils";
import PaymentExpiryTimer from "../Components/Other/Timer";


const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showReviewFormFor, setShowReviewFormFor] = useState(null);
  const [reviewData, setReviewData] = useState({ rating: 0, comment: "" });
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [orderid, setOrderid] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const ordersPerPage = 5;
  const navigate = useNavigate();
  const filter = new Filter();
  const { user, authloading } = useAuth();

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        user && user.role === "admin"
          ? `${process.env.REACT_APP_BACKEND_URL}/orders/all`
          : `${process.env.REACT_APP_BACKEND_URL}/orders`,
        {
          credentials: "include",
        }
      );
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authloading) {
      fetchOrders();
    }
  }, [authloading]);

  useEffect(() => {
    let filtered = orders;

    if (statusFilter) {
      filtered = filtered.filter(
        (order) => order.status.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (orderid) {
      filtered = filtered.filter((order) =>
        order._id.toLowerCase().includes(orderid.toLowerCase())
      );
    }

    if (startDate && endDate) {
      filtered = filtered.filter((order) => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= startDate && orderDate <= endDate;
      });
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  }, [statusFilter, startDate, endDate, orders, orderid]);

  const handleReviewSubmit = async (medicineId) => {
    if (filter.isProfane(reviewData.comment)) {
      toast.warning("Please remove offensive words from your comment.");
      return;
    }

    try {
      if (!reviewData.rating || !reviewData.comment) {
        toast.warning("Please fill in all fields.");
        return;
      }

      const existingReview = selectedOrder.items.some(
        (item) =>
          ((item.medicine._id || item.medicine).toString() === medicineId.toString()) &&
          item.reviews?.some((review) =>
            (review.user._id || review.user).toString() === user._id.toString()
          )
      );
      
      console.log("selectedOrder.items", selectedOrder.items);
      console.log("medicineId", medicineId);
      console.log("userId", user._id);
      console.log("Existing Review:", existingReview);
      

  if (existingReview) {
    toast.warning("You can only add one review per item.");
    return;
  }
      setIsLoading(true);
      await axios.post(
        `/medicines/${medicineId}/review`,
        {
          rating: Number(reviewData.rating),
          comment: reviewData.comment,
        },
        { withCredentials: true }
      );
      setIsLoading(false);
      setShowReviewFormFor(null);
      setReviewData({ rating: 0, comment: "" });
      setIsModalOpen(false);
      setSelectedOrder(null);
      toast.success("Review added successfully!");
    } catch (err) {
      setError(err);
      setIsLoading(false);
      toast.error(err.response?.data?.error || "Failed to add review");
    }
  };

  const indexOfLast = currentPage * ordersPerPage;
  const indexOfFirst = indexOfLast - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const [showDropdown, setShowDropdown] = useState(null);

  const handleRetryPayment = async (orderId) => {
    try {
      setLoading(true);
      const response = await fetch(`/orders/${orderId}/retry-payment`, {
        method: "POST",
        credentials: "include",
      });
      const showToast = (msg, opts) => toast(msg, opts);
      const data = await response.json();
      if (response.ok) {
        loadRazorpay(data.razorpayOrder, data.order, navigate, {
          setLoading,
          showToast,
        });
        setLoading(false);
      } else {
        setLoading(false);
        throw new Error(data.error || "Failed to retry payment");
      }
    } catch (error) {
      toast.error("Error retrying payment", { type: "error" });
      setLoading(false);
    }
  };

  const handleStatusUpdate = (updatedOrder) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order._id === updatedOrder._id ? updatedOrder : order
      )
    );
    setShowDropdown(null);
    setSelectedOrder(updatedOrder);
  };

  return (
    <>
      <HeroImg heading="Orders" text="Home / Orders" />

      {/* Filters */}
      <div className="filters" style={{ padding: "1rem" }}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="Processing">Processing</option>
          <option value="Shipped">Shipped</option>
          <option value="Delivered">Delivered</option>
          <option value="Pending">Pending</option>
          <option value="Cancelled">Cancelled</option>
        </select>

        <DatePicker
          selected={startDate}
          onChange={(date) => setStartDate(date)}
          className="date-picker"
          selectsStart
          startDate={startDate}
          endDate={endDate}
          placeholderText="Start Date"
        />

        <DatePicker
          selected={endDate}
          onChange={(date) => setEndDate(date)}
          selectsEnd
          className="date-picker"
          startDate={startDate}
          endDate={endDate}
          minDate={startDate}
          placeholderText="End Date"
        />

        {user && user.role === "admin" && (
          <input
            type="text"
            placeholder="Search by Order ID"
            onChange={(e) => setOrderid(e.target.value)}
            value={orderid}
          />
        )}

        <button
          onClick={() => {
            setStatusFilter("");
            setStartDate(null);
            setEndDate(null);
            setOrderid("");
          }}
          className="reset-filters"
        >
          Reset Filters
        </button>
      </div>

      {loading ? (
        <div className="loader-container">
          <div className="loader"></div>
        </div>
      ) : !filteredOrders.length ? (
        <div className="no-order">
          <img src="/noorder.png" alt="No order found" />
          <h3>No orders found!!</h3>
          <p>Looks like you haven't placed any orders yet.</p>
          <button className="buy" onClick={() => navigate("/")}>
            Start Shopping
          </button>
          {!user && (
            <div>
              {" "}
              <h3>Have an account?</h3>
              <p style={{ marginTop: "10px" }}>
                <a href="/login">Login</a> to check out faster!!
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="orders-grid">
            {currentOrders.map((order) => (
              <div key={order._id} className="order-card">
                {user && user.role === "admin" && (
                  <div className="edit">
                    <FaPen
                      onClick={() =>
                        setShowDropdown((prevId) =>
                          prevId === order._id ? null : order._id
                        )
                      }
                    />
                  </div>
                )}
                {showDropdown === order._id && (
                  <OrderStatusDropdown
                    orderId={order._id}
                    currentStatus={order.status}
                    onStatusUpdated={handleStatusUpdate}
                  />
                )}
                <h5>Order ID: {order._id}</h5>
                {user && user.role === "admin" && (
                  <div>
                    <p>Owner: {order.owner?.name}</p>
                    <p>Email: {order.owner?.email}</p>
                  </div>
                )}
                <p>Status: {order.status}</p>
                <p>Total Amount: ₹{order.totalPrice}</p>
                <p>Date: {new Date(order.createdAt).toLocaleDateString()}</p>
                {order.status === "pending" && (
                  <PaymentExpiryTimer createdAt={order.createdAt} />
                )}
                <div className="line-wrapper">
                  <span className="line"></span>
                  <span className="dot"></span>
                  <span className="line"></span>
                </div>
                <button
                  className="view-details"
                  onClick={() => {
                    setSelectedOrder(order);
                    setIsModalOpen(true);
                  }}
                >
                  View Details
                </button>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          <div className="pagination">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={i + 1 === currentPage ? "active" : ""}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Modal */}
      {isModalOpen && selectedOrder && (
        <div
          className="modal-overlay active"
          onClick={() => {
            setIsModalOpen(false);
            setShowReviewFormFor(null);
          }}
        >
          {showReviewFormFor !== null && (
            <div
              className="modal-overlay active"
              onClick={(e) => e.stopPropagation()}
            >
              {" "}
            </div>
          )}
          <div
            className="service-modal active"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <span
                className="close-modal"
                onClick={() => {
                  setIsModalOpen(false);
                  setShowReviewFormFor(null);
                }}
              >
                &times;
              </span>
              <h2>{selectedOrder._id}</h2>

              <table className="order-table">
                <thead>
                  <tr>
                    <th>Item Name</th>
                    <th>Quantity</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.medicineName}</td>
                      <td>{item.quantity}</td>
                      <td>
                        {selectedOrder.status === "delivered" && (
                          <>
                            <button
                              className="add-review"
                              onClick={() =>
                                setShowReviewFormFor(item.medicine)
                              }
                            >
                              Add Review
                            </button>

                            {showReviewFormFor === item.medicine && (
                              <div className="review-form">
                                <button
                                  onClick={() => setShowReviewFormFor(null)}
                                  className="close-review-form"
                                  aria-label="Close Review Form"
                                >
                                  &times;
                                </button>
                                <h3>Rate {item.medicineName}</h3>
                                <div className="star-rating">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                      key={star}
                                      style={{
                                        fontSize: "24px",
                                        color:
                                          star <= reviewData.rating
                                            ? "#ffc107"
                                            : "#ccc",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        setReviewData({
                                          ...reviewData,
                                          rating: star,
                                        })
                                      }
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                                <textarea
                                  value={reviewData.comment}
                                  placeholder="Write your comment"
                                  onChange={(e) =>
                                    setReviewData({
                                      ...reviewData,
                                      comment: e.target.value,
                                    })
                                  }
                                  required
                                  rows="3"
                                  style={{ resize: "vertical", width: "100%" }}
                                />
                                <br />
                                <button
                                  onClick={() =>
                                    handleReviewSubmit(item.medicine)
                                  }
                                  className="submit-review"
                                >
                                  {isLoading
                                    ? "Adding Review..."
                                    : "Submit Review"}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p className="price">₹{selectedOrder.totalPrice}</p>
              <p>Payment Mode - {selectedOrder.paymentMethod}</p>

              {selectedOrder.status === "pending" && (
                <div className="payment-buttons">
                  <button
                    className="retry-payment"
                    onClick={() => handleRetryPayment(selectedOrder._id)}
                  >
                    {loading ? "Retrying..." : "Retry Payment"}
                  </button>

                  <button
                    className="cancel"
                    onClick={async () => {
                      await deleteOrder(selectedOrder._id);
                      setIsModalOpen(false);
                      setSelectedOrder(null);
                      await fetchOrders();
                    }}
                  >
                    Delete order
                  </button>
                </div>
              )}
              {selectedOrder.status === "processing" && (
                <button
                  className="cancel"
                  onClick={() => {
                    cancelOrder(selectedOrder._id, fetchOrders);
                    setIsModalOpen(false);
                    setSelectedOrder(null);
                  }}
                >
                  Cancel order
                </button>
              )}

              {selectedOrder.status === "delivered" && (
                <button
                  className="download"
                  onClick={() => {
                    downloadReceipt(selectedOrder._id);
                    setIsModalOpen(false);
                    setSelectedOrder(null);
                  }}
                >
                  Download Receipt
                </button>
              )}

              {/* Tracking */}
              <div style={{ marginTop: "30px" }}>
                <OrderTracking selectedOrder={selectedOrder} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrdersPage;
